/**
 * BRSR Principle 6 (Environment) indicators derived from Arivu organization data.
 * Follows the SEBI BRSR format as revised in 2023 (Essential 1-13, Leadership 1-7).
 *
 * Each row is { key, label, unit, value, status, source }:
 *   status "derived"     - computed from Arivu records (source says which)
 *   status "reported"    - entered directly by the user in Arivu
 *   status "notReported" - Arivu holds no data for it; value is null
 * Missing data is never reported as zero.
 */
import { scope1Categories } from "../../pages/Organization/ScopeCategory";
import { KG_PER_TONNE, toFiniteNumber } from "./formatters";
import { SCOPES, detailMetricValue, summarizeScopes } from "./organizationResults";

export const BRSR_STATUS = { derived: "derived", reported: "reported", notReported: "notReported" };

// Physical conversion constants to gigajoules.
export const ENERGY_UNIT_TO_GJ = {
  gj: 1,
  mj: 1e-3,
  kj: 1e-6,
  kwh: 3.6e-3,
  mwh: 3.6,
  gwh: 3600,
  therm: 0.105506,
  therms: 0.105506,
  mmbtu: 1.055056,
};

// Mass units to tonnes.
export const MASS_UNIT_TO_TONNE = { t: 1, tonne: 1, tonnes: 1, ton: 1, mt: 1, kg: 1e-3, g: 1e-6, lb: 0.00045359237, lbs: 0.00045359237 };

const WASTE_CATEGORY = "5. Waste generated in operations";
const STEAM_CATEGORY = "Steam";
const ELECTRICITY_CATEGORY = "Electricity";
const FUEL_CATEGORIES = scope1Categories.filter((c) => c === "Stationary combustion" || c === "Mobile sources");

const unitKey = (unit) => String(unit ?? "").toLowerCase().replace(/\s+/g, "").trim();
const sameText = (a, b) => String(a ?? "").toLowerCase().trim() === String(b ?? "").toLowerCase().trim();

const notReported = (key, label, unit) => ({ key, label, unit, value: null, status: BRSR_STATUS.notReported, source: "" });
const derived = (key, label, unit, value, source) => ({ key, label, unit, value, status: BRSR_STATUS.derived, source });

/** Sums detail quantities that are already in an energy unit; returns null when none exist. */
const sumEnergyGJ = (detailRows, categories) => {
  const rows = detailRows.filter((r) => categories.some((c) => sameText(c, r.category)) && ENERGY_UNIT_TO_GJ[unitKey(r.unit)] !== undefined);
  if (rows.length === 0) return null;
  return rows.reduce((s, r) => s + r.quantity * ENERGY_UNIT_TO_GJ[unitKey(r.unit)], 0);
};

const sumMassTonnes = (detailRows, category) => {
  const rows = detailRows.filter((r) => sameText(r.category, category) && MASS_UNIT_TO_TONNE[unitKey(r.unit)] !== undefined);
  if (rows.length === 0) return null;
  return rows.reduce((s, r) => s + r.quantity * MASS_UNIT_TO_TONNE[unitKey(r.unit)], 0);
};

const sumPollutantKg = (detailRows, pollutant) => {
  const rows = detailRows.filter((r) => toFiniteNumber(r.pollutantsKg?.[pollutant]) > 0);
  if (rows.length === 0) return null;
  return rows.reduce((s, r) => s + detailMetricValue(r, pollutant), 0);
};

const SOURCE = {
  energyDetails: "Activity data in energy units (kWh, MWh, GJ) from emission details",
  emissions: "Emission inventory (GHG Protocol scopes)",
  pollutantFactors: "Activity data × pollutant emission factors",
  wasteDetails: "Scope 3 Category 5 activity data in mass units",
  netZeroActions: "Net zero reduction actions",
};

/**
 * @param {object} input
 * @param {Array} input.emissionRows - normalizeEmissions output
 * @param {Array} input.detailRows - normalizeEmissionDetails output
 * @param {Array} input.reductionActions - net_zero_tool rows
 * @returns {{ essential: Array, leadership: Array }} indicator groups, each { id, title, rows }
 */
export const buildBrsrPrinciple6 = ({ emissionRows = [], detailRows = [], reductionActions = [] }) => {
  const scopes = summarizeScopes(emissionRows);
  const hasScope = (scope) => emissionRows.some((r) => r.scope === scope);
  const tonnes = (kg) => kg / KG_PER_TONNE;

  const electricity = sumEnergyGJ(detailRows, [ELECTRICITY_CATEGORY]);
  const fuel = sumEnergyGJ(detailRows, FUEL_CATEGORIES);
  const other = sumEnergyGJ(detailRows, [STEAM_CATEGORY]);
  const energyParts = [electricity, fuel, other].filter((v) => v !== null);
  const totalEnergy = energyParts.length ? energyParts.reduce((s, v) => s + v, 0) : null;

  const energyRow = (key, label, value) => (value === null ? notReported(key, label, "GJ") : derived(key, label, "GJ", value, SOURCE.energyDetails));

  const nox = sumPollutantKg(detailRows, "nox");
  const pm10 = sumPollutantKg(detailRows, "pm10");
  const pm25 = sumPollutantKg(detailRows, "pm25");
  const pollutantRow = (key, label, value) => (value === null ? notReported(key, label, "kg") : derived(key, label, "kg", value, SOURCE.pollutantFactors));

  const waste = sumMassTonnes(detailRows, WASTE_CATEGORY);

  const scopeRow = (key, label, scope) =>
    hasScope(scope) ? derived(key, label, "t CO₂e", tonnes(scopes[scope]), SOURCE.emissions) : notReported(key, label, "t CO₂e");

  const actionRows = reductionActions
    .filter((a) => a && (a.action || a.details))
    .map((a, i) => ({
      key: `ghg-project-${i}`,
      label: [a.action, a.category].filter(Boolean).join(" · ") || a.details,
      unit: "t CO₂e",
      value: a.estimated_ghg === null || a.estimated_ghg === undefined || a.estimated_ghg === "" ? null : toFiniteNumber(a.estimated_ghg),
      status: BRSR_STATUS.reported,
      source: [SOURCE.netZeroActions, a.year].filter(Boolean).join(" · "),
    }));

  const essential = [
    {
      id: "EI-1",
      title: "Total energy consumption and energy intensity",
      rows: [
        energyRow("electricity", "Total electricity consumption", electricity),
        energyRow("fuel", "Total fuel consumption", fuel),
        energyRow("otherEnergy", "Energy consumption through other sources", other),
        energyRow("totalEnergy", "Total energy consumption", totalEnergy),
        notReported("renewableShare", "Energy from renewable sources", "GJ"),
        notReported("energyIntensity", "Energy intensity per rupee of turnover", "GJ / ₹"),
      ],
    },
    {
      id: "EI-2",
      title: "Sites identified as designated consumers under the PAT scheme",
      rows: [notReported("patScheme", "PAT scheme applicability and targets", "")],
    },
    {
      id: "EI-3",
      title: "Water withdrawal, consumption and intensity",
      rows: [
        notReported("surfaceWater", "Surface water", "kL"),
        notReported("groundwater", "Groundwater", "kL"),
        notReported("thirdPartyWater", "Third party water", "kL"),
        notReported("seawater", "Seawater / desalinated water", "kL"),
        notReported("otherWater", "Others", "kL"),
        notReported("waterConsumption", "Total water consumption", "kL"),
        notReported("waterIntensity", "Water intensity per rupee of turnover", "kL / ₹"),
      ],
    },
    {
      id: "EI-4",
      title: "Water discharged by destination and treatment level",
      rows: [notReported("waterDischarge", "Total water discharged", "kL")],
    },
    {
      id: "EI-5",
      title: "Zero Liquid Discharge mechanism",
      rows: [notReported("zeroLiquidDischarge", "Zero Liquid Discharge implementation", "")],
    },
    {
      id: "EI-6",
      title: "Air emissions (other than GHG)",
      rows: [
        pollutantRow("nox", "NOx", nox),
        notReported("sox", "SOx", "kg"),
        pollutantRow("pm10", "Particulate matter (PM10)", pm10),
        pollutantRow("pm25", "Particulate matter (PM2.5)", pm25),
        notReported("pop", "Persistent organic pollutants (POP)", "kg"),
        notReported("voc", "Volatile organic compounds (VOC)", "kg"),
        notReported("hap", "Hazardous air pollutants (HAP)", "kg"),
      ],
    },
    {
      id: "EI-7",
      title: "Greenhouse gas emissions (Scope 1 and Scope 2) and intensity",
      rows: [
        scopeRow("scope1", "Total Scope 1 emissions", SCOPES.scope1),
        scopeRow("scope2", "Total Scope 2 emissions", SCOPES.scope2),
        notReported("ghgIntensity", "Scope 1 and 2 emission intensity per rupee of turnover", "t CO₂e / ₹"),
      ],
    },
    {
      id: "EI-8",
      title: "Projects related to reducing greenhouse gas emissions",
      rows: actionRows.length ? actionRows : [notReported("ghgProjects", "GHG reduction projects", "")],
    },
    {
      id: "EI-9",
      title: "Waste management",
      rows: [
        waste === null
          ? notReported("totalWaste", "Total waste generated", "t")
          : derived("totalWaste", "Total waste generated", "t", waste, SOURCE.wasteDetails),
        notReported("plasticWaste", "Plastic waste", "t"),
        notReported("eWaste", "E-waste", "t"),
        notReported("bioMedicalWaste", "Bio-medical waste", "t"),
        notReported("hazardousWaste", "Other hazardous waste", "t"),
        notReported("wasteRecovered", "Waste recovered through recycling, re-use or other recovery", "t"),
        notReported("wasteDisposed", "Waste disposed by incineration, landfilling or other disposal", "t"),
      ],
    },
    {
      id: "EI-10",
      title: "Waste management practices",
      rows: [notReported("wastePractices", "Waste management practices adopted", "")],
    },
    {
      id: "EI-11",
      title: "Operations in ecologically sensitive areas",
      rows: [notReported("ecologicalAreas", "Operations or offices in ecologically sensitive areas", "")],
    },
    {
      id: "EI-12",
      title: "Environmental impact assessments of projects",
      rows: [notReported("eiaProjects", "Environmental impact assessments undertaken", "")],
    },
    {
      id: "EI-13",
      title: "Compliance with environmental law",
      rows: [notReported("environmentalCompliance", "Compliance with applicable environmental laws", "")],
    },
  ];

  const leadership = [
    {
      id: "LI-1",
      title: "Water withdrawal, consumption and discharge in areas of water stress",
      rows: [notReported("waterStress", "Facilities in areas of water stress", "")],
    },
    {
      id: "LI-2",
      title: "Scope 3 emissions and intensity",
      rows: [
        scopeRow("scope3", "Total Scope 3 emissions", SCOPES.scope3),
        notReported("scope3Intensity", "Scope 3 emission intensity per rupee of turnover", "t CO₂e / ₹"),
      ],
    },
    {
      id: "LI-3",
      title: "Impact on biodiversity in ecologically sensitive areas",
      rows: [notReported("biodiversityImpact", "Direct and indirect biodiversity impact", "")],
    },
    {
      id: "LI-4",
      title: "Initiatives to improve resource efficiency or reduce impact",
      rows: actionRows.length ? actionRows : [notReported("resourceInitiatives", "Resource efficiency initiatives", "")],
    },
    {
      id: "LI-5",
      title: "Business continuity and disaster management plan",
      rows: [notReported("disasterManagement", "Business continuity and disaster management plan", "")],
    },
    {
      id: "LI-6",
      title: "Significant adverse environmental impact from the value chain",
      rows: [notReported("valueChainImpact", "Adverse impact and mitigation measures", "")],
    },
    {
      id: "LI-7",
      title: "Value chain partners assessed for environmental impacts",
      rows: [notReported("valueChainAssessment", "Share of value chain partners assessed (by value of business)", "%")],
    },
  ];

  return { essential, leadership };
};

/** Share (0-100) of indicator rows that hold data. */
export const brsrCompleteness = ({ essential = [], leadership = [] }) => {
  const rows = [...essential, ...leadership].flatMap((i) => i.rows);
  if (rows.length === 0) return 0;
  return (rows.filter((r) => r.status !== BRSR_STATUS.notReported).length / rows.length) * 100;
};

/** Indicators with at least one missing row, for the data-gap checklist. */
export const brsrDataGaps = ({ essential = [], leadership = [] }) =>
  [...essential, ...leadership]
    .map((i) => ({ id: i.id, title: i.title, missing: i.rows.filter((r) => r.status === BRSR_STATUS.notReported).map((r) => r.label) }))
    .filter((i) => i.missing.length > 0);
